import axios from 'axios';
import urls from '@/configs/urls';
import { router } from '@/router';
import { useAppStore, useAuthStore } from '@/stores';
import { showErrorToast } from '@/utils/helper';

const axiosInstance = axios.create({
  baseURL: urls.getParam('BASE_URL'),
  timeout: 10_000,
  headers: { 'Content-Type': 'application/json' },
});

/* ———————————————————————————————
 *  Helper functions
 * ——————————————————————————————— */
const getAccessToken = () => localStorage.getItem('token');

function clearAuth() {
  localStorage.removeItem('token');
  const auth = useAuthStore();
  auth.token = null;
  router.replace({ name: 'login' });
}

const refreshAccessToken = async () => {
  try {
    const { data } = await axiosInstance.post('/auth/refresh', {
      token: getAccessToken(),
    });
    localStorage.setItem('token', data.accessToken);
    return data.accessToken;
  } catch {
    clearAuth();
    throw new Error('Token refresh failed');
  }
};

/* ———————————————————————————————
 *  Request interceptor
 * ——————————————————————————————— */
axiosInstance.interceptors.request.use(
  (config) => {
    useAppStore().showLoading(true);

    if (!config.noAuth) {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    useAppStore().showLoading(false);
    return Promise.reject(error);
  },
);

/* ———————————————————————————————
 *  Response interceptor
 * ——————————————————————————————— */
axiosInstance.interceptors.response.use(
  (response) => {
    useAppStore().showLoading(false);
    return response;
  },
  async (error) => {
    useAppStore().showLoading(false);

    const original = error.config;

    // Network Error
    if (!error.response) {
      showErrorToast(`Şəbəkə xətası: ${error.message}`);
      clearAuth();
      return Promise.reject(error);
    }

    // 400 / 422 → Show errors
    if ([400, 422].includes(error.response.status)) {
      showErrorToast(error.response.data.message);
      return Promise.reject(error);
    }

    // 401 → refresh dene
    if (error.response.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const newToken = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(original);
      } catch (e) {
        return Promise.reject(e);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
